import { useState, useMemo } from 'react';
import { PlusIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { PostalCodeField } from '@makemydeal/dr-shared-components/postal-code-field';
import { Dropdown } from '@makemydeal/dr-shared-components';
import { getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { FieldsRow } from '../sectionLayout.styles';
import CustomerFieldsLayoutHeader from './CustomerFieldsLayoutHeader';
import { DurationField } from './DurationField';
import { ExpandedSectionCard } from './housingAndEmployment.styles';
import {
    YEARS_AT_PREVIOUS_ADDRESS_FIELD,
    MONTHS_AT_PREVIOUS_ADDRESS_FIELD,
    hasPreviousAddressData
} from './housingAndEmployment.utils';
import { DEFAULT_ERROR_STATE, type ErrorState } from '@makemydeal/dr-dash-ui-utils';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';

const PII_INPUT_ATTR = { textInput: { dataAttr: { className: 'pii' } } };

export const PreviousAddress = () => {
    const {
        formValues,
        formErrors,
        handleInputChange,
        handleInitPreviousAddress,
        handleClearPreviousAddress,
        isLocked,
        customerRole,
        onAnalyticsEvent
    } = useCustomerCardContext();
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    const [expanded, setExpanded] = useState(() =>
        hasPreviousAddressData(
            formValues.previousAddress as Record<string, unknown> | undefined,
            formValues.yearsAtPreviousAddress,
            formValues.monthsAtPreviousAddress
        )
    );

    const stateOptions = useMemo(() => getStateCodesSelectInputValues(), []);
    const previousAddress = formValues.previousAddress;
    const prevErrors = formErrors?.previousAddress as Record<string, ErrorState> | undefined;

    const errors = useMemo(
        () => ({
            streetAddress: prevErrors?.streetAddress ?? DEFAULT_ERROR_STATE,
            streetAddress2: prevErrors?.streetAddress2 ?? DEFAULT_ERROR_STATE,
            city: prevErrors?.city ?? DEFAULT_ERROR_STATE,
            state: prevErrors?.state ?? DEFAULT_ERROR_STATE,
            zip: prevErrors?.zip ?? DEFAULT_ERROR_STATE,
            county: prevErrors?.county ?? DEFAULT_ERROR_STATE,
            yearsAtPreviousAddress: (formErrors as Record<string, ErrorState>)?.yearsAtPreviousAddress ?? DEFAULT_ERROR_STATE,
            monthsAtPreviousAddress: (formErrors as Record<string, ErrorState>)?.monthsAtPreviousAddress ?? DEFAULT_ERROR_STATE
        }),
        [prevErrors, formErrors]
    );

    const handleAddressChange = (field: string, value: string) => {
        handleInputChange(`previousAddress.${field}`, value);
    };

    const handleDelete = () => {
        handleClearPreviousAddress();
        setExpanded(false);
    };

    return (
        <div data-testid="previous-address-section">
            {!expanded ? (
                <Action
                    data-testid="previous-address-button"
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                    sx={{ marginTop: '1rem' }}
                    onClick={() => {
                        onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_PREVIOUS_ADDRESS, { customerRole });
                        handleInitPreviousAddress();
                        setExpanded(true);
                    }}
                >
                    Add Previous Address
                </Action>
            ) : (
                <ExpandedSectionCard>
                    <CustomerFieldsLayoutHeader
                        onDelete={handleDelete}
                        deleteTestId="previous-address-delete-button"
                        disabled={isLocked}
                    >
                        Previous Address
                    </CustomerFieldsLayoutHeader>

                    <FieldsRow $columns={2} $mdColumns={1} $smColumns={1}>
                        <TextInput
                            name="previousStreetAddress"
                            displayLabel
                            label="Street Address"
                            value={previousAddress?.streetAddress ?? ''}
                            onChange={(e) => handleAddressChange('streetAddress', e.target.value)}
                            hasError={errors.streetAddress.hasError}
                            errorMessage={errors.streetAddress.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            slotProps={PII_INPUT_ATTR}
                            data-testid="customer-form-previous-street-address"
                        />

                        <TextInput
                            name="previousStreetAddress2"
                            displayLabel
                            label="Street Address 2"
                            value={previousAddress?.streetAddress2 ?? ''}
                            onChange={(e) => handleAddressChange('streetAddress2', e.target.value)}
                            hasError={errors.streetAddress2.hasError}
                            errorMessage={errors.streetAddress2.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            slotProps={PII_INPUT_ATTR}
                            data-testid="customer-form-previous-street-address-2"
                        />
                    </FieldsRow>

                    <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                        <TextInput
                            name="previousCity"
                            displayLabel
                            label="City"
                            value={previousAddress?.city ?? ''}
                            onChange={(e) => handleAddressChange('city', e.target.value)}
                            hasError={errors.city.hasError}
                            errorMessage={errors.city.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            slotProps={PII_INPUT_ATTR}
                            data-testid="customer-form-previous-city"
                        />

                        <Dropdown
                            name="previousState"
                            displayLabel
                            label="State"
                            value={previousAddress?.state ?? ''}
                            onChange={(e) => handleAddressChange('state', String(e.target.value))}
                            options={stateOptions}
                            hasError={errors.state.hasError}
                            errorMessage={errors.state.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            allowRemoval
                            data-testid="customer-form-previous-state"
                        />

                        <PostalCodeField
                            name="previousZip"
                            displayLabel
                            label="Postal Code"
                            value={previousAddress?.zip ?? ''}
                            onChange={(e) => handleAddressChange('zip', e.target.value)}
                            hasError={errors.zip.hasError}
                            errorMessage={errors.zip.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            slotProps={PII_INPUT_ATTR}
                            data-testid="customer-form-previous-zip"
                        />

                        <TextInput
                            name="previousCounty"
                            displayLabel
                            label="County"
                            value={previousAddress?.county ?? ''}
                            onChange={(e) => handleAddressChange('county', e.target.value)}
                            hasError={errors.county.hasError}
                            errorMessage={errors.county.errorMessage}
                            selectOnFocusSelector={selectOnFocusSelector}
                            disabled={isLocked}
                            size="small"
                            data-testid="customer-form-previous-county"
                        />

                        <DurationField
                            label="Yrs. at Previous Address"
                            field={YEARS_AT_PREVIOUS_ADDRESS_FIELD}
                            value={formValues.yearsAtPreviousAddress}
                            error={errors.yearsAtPreviousAddress}
                        />

                        <DurationField
                            label="Mos. at Previous Address"
                            field={MONTHS_AT_PREVIOUS_ADDRESS_FIELD}
                            value={formValues.monthsAtPreviousAddress}
                            error={errors.monthsAtPreviousAddress}
                        />
                    </FieldsRow>
                </ExpandedSectionCard>
            )}
        </div>
    );
};

export default PreviousAddress;
